import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../components/Toast';
import { listTickets, createTicket } from '../api/support.api';
import { listOrders } from '../api/order.api';
import EmptyState from '../components/EmptyState';

const ticketStatusColors = {
    open: 'bg-blue-100 text-blue-800',
    reopened: 'bg-cyan-100 text-cyan-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    resolved: 'bg-green-100 text-green-800',
    closed: 'bg-muted text-foreground',
};

export default function SupportTickets() {
    const navigate = useNavigate();

    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Create form state
    const [showForm, setShowForm] = useState(false);
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [orderId, setOrderId] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [recentOrders, setRecentOrders] = useState([]);

    useEffect(() => {
        document.title = 'Support Tickets | Fone Express';
    }, []);

    const fetchTickets = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await listTickets();
            const resData = res?.data || res;
            if (resData?.isOk && resData?.data) {
                setTickets(resData.data.tickets || resData.data || []);
            } else {
                setTickets([]);
            }
        } catch (err) {
            console.error('Error fetching tickets:', err);
            setError('Unable to load support tickets. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchRecentOrders = useCallback(async () => {
        try {
            const res = await listOrders({ page: 1, limit: 20 });
            const resData = res?.data || res;
            if (resData?.isOk && resData?.data) {
                setRecentOrders(resData.data.orders || []);
            }
        } catch (err) {
            console.error('Error fetching orders for ticket form:', err);
        }
    }, []);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    useEffect(() => {
        if (showForm) {
            fetchRecentOrders();
        }
    }, [showForm, fetchRecentOrders]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!subject.trim()) {
            toast.error('Please enter a subject');
            return;
        }
        if (!message.trim()) {
            toast.error('Please enter a message');
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                subject: subject.trim(),
                message: message.trim(),
            };
            if (orderId) payload.orderId = orderId;
            const res = await createTicket(payload);
            const resData = res?.data || res;
            if (resData?.isOk) {
                toast.success('Support ticket created successfully');
                setSubject('');
                setMessage('');
                setOrderId('');
                setShowForm(false);
                fetchTickets();
            } else {
                toast.error(resData?.message || 'Failed to create ticket');
            }
        } catch (err) {
            console.error('Error creating ticket:', err);
            toast.error('Failed to create support ticket. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const formatStatus = (status) => {
        if (status === 'reopened') return 'Re-Open';
        return (status || '')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const renderStatusTags = (status) => {
        if (status === 'reopened') {
            return (
                <>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ticketStatusColors.reopened}`}>
                        Re-Open
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ticketStatusColors.in_progress}`}>
                        In Progress
                    </span>
                </>
            );
        }
        return (
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ticketStatusColors[status] || 'bg-muted text-foreground'}`}>
                {formatStatus(status)}
            </span>
        );
    };

    return (
        <div>
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Support Tickets</h1>
                    <p className="text-muted-foreground text-sm mt-1">Get help from our support team</p>
                </div>
                <button
                    onClick={() => setShowForm(!showForm)}
                    className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:brightness-110 transition-all whitespace-nowrap self-start"
                >
                    {showForm ? (
                        <>
                            <i className="ri-close-line mr-1"></i>
                            Cancel
                        </>
                    ) : (
                        <>
                            <i className="ri-add-line mr-1"></i>
                            New Ticket
                        </>
                    )}
                </button>
            </div>

            {/* Create Ticket Form */}
            {showForm && (
                <div className="bg-card border border-border rounded-lg p-6 mb-6">
                    <h2 className="text-lg font-semibold text-foreground mb-4">Create New Ticket</h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">
                                Subject <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder="Brief description of your issue"
                                className="w-full border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">
                                Related Order (optional)
                            </label>
                            <select
                                value={orderId}
                                onChange={(e) => setOrderId(e.target.value)}
                                className="w-full border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary"
                            >
                                <option value="">No related order</option>
                                {recentOrders.map((order) => (
                                    <option key={order._id || order.orderNumber} value={order._id || order.orderNumber}>
                                        #{order.orderNumber} - {new Date(order.createdAt).toLocaleDateString('en-KE', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">
                                Message <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                rows={5}
                                placeholder="Describe your issue in detail..."
                                className="w-full border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary resize-none"
                            ></textarea>
                        </div>
                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={submitting}
                                className="px-6 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:brightness-110 transition-all disabled:opacity-50 whitespace-nowrap"
                            >
                                {submitting ? (
                                    <>
                                        <i className="ri-loader-4-line animate-spin mr-1"></i>
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <i className="ri-send-plane-line mr-1"></i>
                                        Submit Ticket
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Loading Skeleton */}
            {loading && (
                <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                        <div key={i} className="bg-card border border-border rounded-lg p-4 animate-pulse">
                            <div className="flex justify-between items-start mb-3">
                                <div className="space-y-2 flex-1">
                                    <div className="h-4 bg-muted rounded w-48"></div>
                                    <div className="h-3 bg-muted rounded w-64"></div>
                                </div>
                                <div className="h-6 bg-muted rounded-full w-20"></div>
                            </div>
                            <div className="h-3 bg-muted rounded w-full"></div>
                        </div>
                    ))}
                </div>
            )}

            {/* Error State */}
            {!loading && error && (
                <div className="text-center py-12">
                    <i className="ri-error-warning-line text-4xl text-red-400 mb-4 block"></i>
                    <p className="text-muted-foreground mb-4">{error}</p>
                    <button
                        onClick={fetchTickets}
                        className="px-6 py-2 bg-primary text-white rounded-lg hover:brightness-110 transition-all whitespace-nowrap"
                    >
                        Try Again
                    </button>
                </div>
            )}

            {/* Empty State */}
            {!loading && !error && tickets.length === 0 && (
                <EmptyState
                    icon="ri-customer-service-line"
                    title="No support tickets"
                    description="You haven't created any support tickets yet. Need help? Click the button above to create a new ticket."
                    actionLabel="Create Ticket"
                    onAction={() => setShowForm(true)}
                />
            )}

            {/* Ticket List */}
            {!loading && !error && tickets.length > 0 && (
                <div className="space-y-4">
                    {tickets.map((ticket) => (
                        <div
                            key={ticket._id || ticket.ticketNumber}
                            onClick={() => navigate(`/account/support/${ticket.ticketNumber}`)}
                            className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                        >
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className="text-xs font-mono text-muted-foreground">
                                            {ticket.ticketNumber}
                                        </p>
                                        {renderStatusTags(ticket.status)}
                                    </div>
                                    <h3 className="font-medium text-foreground mt-1 line-clamp-1">
                                        {ticket.subject}
                                    </h3>
                                </div>
                                <p className="text-xs text-muted-foreground whitespace-nowrap">
                                    {new Date(ticket.createdAt).toLocaleDateString('en-KE', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                    })}
                                </p>
                            </div>

                            {/* Last message preview */}
                            {ticket?.lastMessage && (
                                <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
                                    {typeof ticket.lastMessage === 'string' ? ticket.lastMessage : ticket.lastMessage?.message || ''}
                                </p>
                            )}

                            {/* Message count */}
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <i className="ri-chat-3-line"></i>
                                <span>
                                    {ticket?.messageCount || ticket?.messages?.length || 0} message{(ticket?.messageCount || ticket?.messages?.length || 0) !== 1 ? 's' : ''}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
